import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DownloadIcon from '@mui/icons-material/Download';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import axios from 'axios';
import { API_BASE } from '../api/client';
import { markdownPreviewSx } from '../components/markdownPreviewSx';
import type { ProjectDocument } from '../types';

const PREVIEWABLE_MIME = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'text/markdown',
  'text/html',
];

/**
 * Trang công khai (không cần đăng nhập) hiển thị 1 tài liệu dự án đã được
 * chia sẻ qua token — xem /api/shared-documents/:token ở projectDocuments.ts.
 * KHÔNG dùng `api` (axios client dùng chung): interceptor của nó đá 401 về
 * /login, không phù hợp cho trang public — dùng axios trần, gọi thẳng API_BASE.
 */
export function SharedDocumentPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [doc, setDoc] = useState<ProjectDocument | null | undefined>(undefined);
  const [markdownText, setMarkdownText] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let active = true;
    axios
      .get<ProjectDocument>(`${API_BASE}/shared-documents/${token}`)
      .then((res) => {
        if (active) setDoc(res.data);
      })
      .catch(() => {
        if (active) setDoc(null);
      });
    return () => {
      active = false;
    };
  }, [token]);

  useEffect(() => {
    if (!doc || doc.external_url || doc.mime !== 'text/markdown') return;
    let active = true;
    axios
      .get<string>(`${API_BASE}/shared-documents/${token}/raw`, { responseType: 'text' })
      .then((res) => {
        if (active) setMarkdownText(res.data);
      })
      .catch(() => {
        if (active) setMarkdownText(null);
      });
    return () => {
      active = false;
    };
  }, [doc, token]);

  const rawUrl = `${API_BASE}/shared-documents/${token}/raw`;

  /**
   * `<a download href={rawUrl}>` không hoạt động: thuộc tính `download` của
   * trình duyệt chỉ ép tải xuống khi URL CÙNG ORIGIN với trang — `rawUrl` trỏ
   * sang origin của Worker API (khác origin trang web Pages), nên trình
   * duyệt bỏ qua `download` và coi như một link điều hướng bình thường (nhảy
   * sang hiển thị nội dung thô thay vì tải file). Tải blob về rồi tạo
   * `blob:` URL (luôn cùng origin với trang vừa tạo ra nó) để ép tải xuống
   * đúng cách — giống `handleDownload` trong ProjectDocumentsPage.tsx.
   */
  const handleDownload = async () => {
    if (!doc) return;
    try {
      const res = await axios.get<Blob>(rawUrl, { responseType: 'blob' });
      const blobUrl = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = doc.file_name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
    } catch {
      // Trang public, không có hệ thống notify — im lặng là chấp nhận được,
      // link "Mở liên kết"/xem trước vẫn còn nếu tải thất bại.
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', p: 2 }}>
      <Paper elevation={2} sx={{ maxWidth: 1400, mx: 'auto', borderRadius: 3, p: 4 }}>
        {doc === undefined && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {doc === null && (
          <Typography color="text.secondary" textAlign="center">
            Liên kết không tồn tại hoặc đã bị thu hồi.
          </Typography>
        )}
        {doc && (
          <>
            <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
              {doc.title}
            </Typography>
            {doc.external_url ? (
              <Button
                variant="contained"
                startIcon={<OpenInNewIcon />}
                component="a"
                href={doc.external_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Mở liên kết
              </Button>
            ) : (
              <>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => void handleDownload()}
                  sx={{ mb: 2 }}
                >
                  Tải xuống
                </Button>
                <Box sx={{ mt: 2 }}>
                  {doc.mime === 'application/pdf' && (
                    <Box
                      component="iframe"
                      src={rawUrl}
                      title={doc.title}
                      sx={{ width: '100%', height: '75vh', border: 'none' }}
                    />
                  )}
                  {doc.mime === 'text/html' && (
                    // sandbox="" chặn hoàn toàn script — trang này public,
                    // không đăng nhập, HTML do user upload có thể chứa
                    // <script> tuỳ ý.
                    <Box
                      component="iframe"
                      sandbox=""
                      src={rawUrl}
                      title={doc.title}
                      sx={{ width: '100%', height: '75vh', border: 'none' }}
                    />
                  )}
                  {(doc.mime === 'image/png' || doc.mime === 'image/jpeg' || doc.mime === 'image/webp') && (
                    <Box
                      component="img"
                      src={rawUrl}
                      alt={doc.title}
                      sx={{ maxWidth: '100%', display: 'block', mx: 'auto' }}
                    />
                  )}
                  {doc.mime === 'text/markdown' && markdownText && (
                    <Box sx={markdownPreviewSx}>
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdownText}</ReactMarkdown>
                    </Box>
                  )}
                  {!PREVIEWABLE_MIME.includes(doc.mime) && (
                    <Typography color="text.secondary">
                      Không xem trước được loại tệp này — vui lòng tải xuống.
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}
